import {
  CheckCircleOutlineIcon,
  ExclamationOutlineIcon,
  Icon,
  InformationCircleOutlineIcon,
  XOutlineIcon,
} from '@fountain/ripple/icon';
import {
  Grid,
  IconButton,
  Slide,
  Snackbar,
  SnackbarContent,
  withStyles,
} from '@material-ui/core';
import { TransitionProps } from '@material-ui/core/transitions/transition';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose, Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import globalMessages from 'shared/global/messages';
import { useInjectReducer } from 'utils/injectReducer';

import { addMessageAction, removeMessageAction, Variant } from './actions';
import reducer from './reducer';
import { makeSelectMessages } from './selectors';
import styles from './styles';

const AUTO_CLOSE_MS = 5000;

const icons = {
  error: { icon: ExclamationOutlineIcon, color: 'rose' },
  warning: { icon: ExclamationOutlineIcon, color: 'amber' },
  info: { icon: InformationCircleOutlineIcon, color: 'blue' },
  success: { icon: CheckCircleOutlineIcon, color: 'green' },
} as const;

interface Message {
  id: number;
  text: string;
  variant: Variant;
  autoClose: boolean;
}

interface FlashMessageProps {
  flashMessages: Message[];
  classes: Record<string, string>;
  handleClose: (messageId: Message['id']) => void;
}

// Declared once: a transition created during render is a new component type
// on every render, so each new message would remount every toast, and with
// it the Close button holding focus.
const SlideDown = (props: TransitionProps) => (
  <Slide {...props} direction="down" />
);

interface ToastProps {
  message: Message;
  paused: boolean;
  classes: Record<string, string>;
  handleClose: FlashMessageProps['handleClose'];
}

const Toast: React.FC<ToastProps> = ({
  message,
  paused,
  classes,
  handleClose,
}) => {
  const intl = useIntl();
  const { id, autoClose } = message;
  // What is left of this toast's 5 s. A pause stops the count and the
  // resume carries on from here, so a hover neither restarts nor ends it.
  const remaining = React.useRef(AUTO_CLOSE_MS);

  React.useEffect(() => {
    if (!autoClose || paused) return undefined;
    const startedAt = performance.now();
    const timer = setTimeout(() => handleClose(id), remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current -= performance.now() - startedAt;
    };
  }, [autoClose, handleClose, id, paused]);

  return (
    <SnackbarContent
      message={
        <Grid
          container
          direction="row"
          wrap="nowrap"
          justify="center"
          className={classes.message}
        >
          <Icon
            {...icons[message.variant]}
            size="sm"
            className={classes.icon}
          />
          {message.text}
        </Grid>
      }
      className={classes[message.variant]}
      classes={{
        root: classes.root,
        action: classes.action,
      }}
      action={[
        <IconButton
          key="close"
          aria-label={intl.formatMessage(globalMessages.close)}
          color="inherit"
          className={classes.button}
          onClick={() => handleClose(id)}
        >
          <Icon
            icon={XOutlineIcon}
            size="lg"
            color="inherit"
            className={classes.close}
          />
        </IconButton>,
      ]}
    />
  );
};

export const FlashMessage: React.FC<FlashMessageProps> = ({
  flashMessages,
  classes,
  handleClose,
}) => {
  useInjectReducer({ key: 'flashMessage', reducer });

  // The whole stack pauses, not only the toast under the pointer: a toast
  // above it expiring would slide the rest up out from under the pointer.
  const stack = React.useRef<HTMLDivElement>(null);
  const pointer = React.useRef({ x: 0, y: 0 });
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const hasToasts = flashMessages.length > 0;

  // Hover follows real pointer movement, read on the document rather than
  // from the stack's own enter and leave. A toast that appears under a
  // resting pointer then still closes, and a browser that fires no
  // `mouseleave` when the element under the pointer is removed cannot leave
  // the pause stuck on. A touch is skipped: a tap has no pointer to move
  // away, so it would hold the stack until the next tap.
  React.useEffect(() => {
    if (!hasToasts) return undefined;
    const followPointer = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      pointer.current = { x: event.clientX, y: event.clientY };
      setHovered(!!stack.current?.contains(event.target as Node));
    };
    document.addEventListener('pointermove', followPointer);
    return () => document.removeEventListener('pointermove', followPointer);
  }, [hasToasts]);

  // Closing a toast reshapes the stack under a pointer that has not moved,
  // and removes the element holding focus without a blur. Neither fires an
  // event, so check both against the stack as it now is. The check only
  // ever ends a pause; starting one is left to the events.
  React.useLayoutEffect(() => {
    const box = stack.current?.getBoundingClientRect();
    const { x, y } = pointer.current;
    setHovered(
      wasHovered =>
        wasHovered &&
        !!box &&
        x >= box.left &&
        x < box.right &&
        y >= box.top &&
        y < box.bottom,
    );
    setFocused(
      wasFocused =>
        wasFocused && !!stack.current?.contains(document.activeElement),
    );
  }, [flashMessages]);

  if (!hasToasts) return null;

  return (
    <Snackbar
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      open={hasToasts}
      TransitionComponent={SlideDown}
    >
      <Grid
        ref={stack}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={event =>
          setFocused(
            event.currentTarget.contains(event.relatedTarget as Node | null),
          )
        }
      >
        {flashMessages.map(message => (
          <Toast
            key={message.id}
            message={message}
            paused={hovered || focused}
            classes={classes}
            handleClose={handleClose}
          />
        ))}
      </Grid>
    </Snackbar>
  );
};

const mapStateToProps = createStructuredSelector({
  flashMessages: makeSelectMessages(),
});

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    addMessage: (type: unknown, text: string) =>
      dispatch(addMessageAction(text)),
    handleClose: (id: number) => dispatch(removeMessageAction(id)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(
  withConnect,
  React.memo,
  // @ts-expect-error this should be resolved alongside converting `styles.js`
  withStyles(styles),
)(FlashMessage);
